import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { 
  Award, 
  Lock, 
  Loader2,
  Download,
  Edit3,
  Check,
  X,
  Sparkles,
  TrendingUp,
  Trophy,
  Lightbulb,
  Target,
  Heart,
  Star,
  Compass,
  Calendar
} from 'lucide-react';

const sectionIcons = {
  personal_reflections: Heart,
  business_evolution: TrendingUp,
  vision_lab_progress: Award,
  biggest_accomplishments: Trophy,
  challenges_overcome: Target,
  key_insights: Lightbulb,
  improvement_areas: Compass,
  next_year_vision: Star
};

const AnnualReport = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [report, setReport] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    checkEligibility();
    fetchReport();
  }, []);

  const checkEligibility = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/annual-report/eligibility`, { headers });
      setEligibility(response.data);
    } catch (error) {
      console.error('Error checking eligibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/annual-report/latest`, { headers });
      if (response.data.report) {
        setReport(response.data.report);
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/annual-report/generate`, {}, { headers });
      setReport(response.data);
      toast.success('Annual Report generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/annual-report/${report.report_id}/pdf`, {
        headers,
        
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Vision_Lab_Annual_Report_${report.year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  const startEditing = (sectionKey, content) => {
    setEditingSection(sectionKey);
    setEditContent(content);
  };

  const saveEdit = async () => {
    if (!editingSection || !report) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/annual-report/${report.report_id}/section`, {
        section_key: editingSection,
        content: editContent
      }, { headers });
      
      // Update local state
      setReport({
        ...report,
        [editingSection]: {
          ...report[editingSection],
          content: editContent
        }
      });
      
      toast.success('Section updated!');
      setEditingSection(null);
      setEditContent('');
    } catch (error) {
      toast.error('Failed to save changes');
    }
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditContent('');
  };

  if (loading) {
    return (
      <DashboardLayout title="Vision Lab Annual Report">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
        </div>
      </DashboardLayout>
    );
  }

  // Locked State
  if (!eligibility?.eligible && !report) {
    const progress = eligibility?.days_since_signup ? (eligibility.days_since_signup / 365) * 100 : 0;
    
    return (
      <DashboardLayout title="Vision Lab Annual Report">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
              <Lock className="text-[#52525B]" size={40} />
            </div>
            
            <h2 className="font-serif text-3xl text-white mb-4">
              Vision Lab Annual Report
            </h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Your comprehensive annual brand journey report unlocks after 12 months of building your vision with us.
            </p>

            <div className="bg-[#1C1C1C] p-6 mb-8 max-w-sm mx-auto">
              <div className="flex items-center justify-between mb-4">
                <span className="text-[#A1A1AA] text-sm">Journey Progress</span>
                <span className="text-[#D4AF37] font-accent">
                  {eligibility?.days_since_signup || 0} / 365 days
                </span>
              </div>
              <Progress 
                value={Math.min(progress, 100)} 
                className="h-2 bg-[#27272A]"
              />
              <div className="flex items-center gap-2 mt-4 justify-center">
                <Calendar size={14} className="text-[#52525B]" />
                <p className="text-[#52525B] text-xs">
                  Unlocks on {new Date(eligibility?.unlock_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>

            <p className="text-[#E5C585] text-sm">
              {eligibility?.message}
            </p>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Vision Lab Annual Report">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <Award className="text-[#D4AF37] mb-4" size={36} />
              <h2 className="font-serif text-2xl text-white mb-2">
                Your Annual Brand Journey Report
              </h2>
              <p className="text-[#A1A1AA]">
                A comprehensive reflection on your year of brand building
              </p>
            </div>
            
            <div className="flex gap-3">
              {report && (
                <Button
                  onClick={handleDownloadPDF}
                  disabled={downloading}
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-4"
                  data-testid="download-pdf"
                >
                  {downloading ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <Download className="mr-2" size={18} />
                  )}
                  Download PDF
                </Button>
              )}
              <Button
                onClick={handleGenerateReport}
                disabled={generating}
                className="btn-gold px-6 py-4"
                data-testid="generate-report"
              >
                {generating ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2" size={18} />
                    {report ? 'Regenerate' : 'Generate Report'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>

        {report ? (
          <div className="space-y-6">
            {/* Highlight Quote */}
            {report.highlight_quote && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card-premium p-8 text-center bg-gradient-to-br from-[#D4AF37]/10 to-transparent"
              >
                <Sparkles className="text-[#D4AF37] mx-auto mb-4" size={28} />
                <p className="font-serif text-2xl text-white italic leading-relaxed">
                  "{report.highlight_quote}"
                </p>
              </motion.div>
            )}

            {/* Overall Score */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card-premium p-8 text-center"
            >
              <span className="text-[#E5C585] text-xs font-accent tracking-widest">
                YEAR {report.year} BRAND JOURNEY SCORE
              </span>
              <div className="font-serif text-8xl text-[#D4AF37] mt-4">
                {report.overall_score}/10
              </div>
            </motion.div>

            {/* Report Sections */}
            {Object.entries(sectionIcons).map(([key, Icon], index) => {
              const section = report[key];
              if (!section) return null;
              
              const isEditing = editingSection === key;
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.05 }}
                  className="card-premium p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#1C1C1C] flex items-center justify-center">
                        <Icon className="text-[#D4AF37]" size={20} />
                      </div>
                      <h3 className="font-serif text-xl text-white">{section.title}</h3>
                    </div>
                    
                    {!isEditing ? (
                      <button
                        onClick={() => startEditing(key, section.content)}
                        className="text-[#52525B] hover:text-[#D4AF37] transition-colors p-2"
                        title="Edit section"
                        data-testid={`edit-${key}`}
                      >
                        <Edit3 size={16} />
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={saveEdit}
                          className="text-green-400 hover:text-green-300 transition-colors p-2"
                          title="Save"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-red-400 hover:text-red-300 transition-colors p-2"
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <AnimatePresence mode="wait">
                    {isEditing ? (
                      <motion.div
                        key="editing"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-4 min-h-[150px] text-white"
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="viewing"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-[#A1A1AA] leading-relaxed whitespace-pre-wrap"
                      >
                        {section.content}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}

            {/* Report Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-center justify-center gap-4 text-[#52525B] text-sm"
            >
              <span>Generated {new Date(report.generated_at).toLocaleDateString()}</span>
              {report.is_edited && (
                <>
                  <span>•</span>
                  <span>Edited {new Date(report.last_edited).toLocaleDateString()}</span>
                </>
              )}
            </motion.div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <Award className="text-[#27272A] mx-auto mb-6" size={64} />
            <h3 className="font-serif text-xl text-white mb-3">No Report Yet</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto">
              Click "Generate Report" to create your comprehensive annual brand journey report.
            </p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AnnualReport;
